import { useState, useEffect, useCallback, useRef } from "react";
import {
  isTelegramMiniApp,
  launchParams,
  requestFullscreen,
  exitFullscreen,
  isAppFullscreen,
  onEvent,
  offEvent,
  setPlayerFullscreenState,
} from "core/utils/telegram";

export default function useTelegramPlayerFullscreen() {
  const [isPlayerFullscreen, setIsPlayerFullscreen] = useState(false);
  const wasAppFullscreenRef = useRef(false);

  const hasTelegramFallback =
    isTelegramMiniApp && !["ios"].includes(launchParams?.platform);
  const isDesktopPlatform =
    isTelegramMiniApp &&
    !["android", "android_x", "ios"].includes(launchParams?.platform);

  const handleExitFullscreen = useCallback(() => {
    if (wasAppFullscreenRef.current) {
      // Приложение было в fullscreen до нас - оставляем его
      setIsPlayerFullscreen(false);
    } else {
      // Мы сами включили fullscreen - выходим
      exitFullscreen();
      setIsPlayerFullscreen(false);
    }
    wasAppFullscreenRef.current = false;
  }, []);

  const togglePlayerFullscreen = useCallback(() => {
    if (!hasTelegramFallback) return false;

    if (isPlayerFullscreen) {
      handleExitFullscreen();
      return true;
    }

    const wasFullscreen = isAppFullscreen();
    wasAppFullscreenRef.current = wasFullscreen;

    if (!wasFullscreen) {
      const success = requestFullscreen();
      if (success) setIsPlayerFullscreen(true);
      return success;
    }

    // Приложение уже в fullscreen - просто включаем UI плеера
    setIsPlayerFullscreen(true);
    return true;
  }, [isPlayerFullscreen, hasTelegramFallback, handleExitFullscreen]);

  // Слушаем fullscreen-события от Telegram
  useEffect(() => {
    if (!hasTelegramFallback) return;

    const onChanged = (eventData) => {
      if (!eventData.is_fullscreen && isPlayerFullscreen) {
        setIsPlayerFullscreen(false);
        wasAppFullscreenRef.current = false;
      }
    };

    const onFailed = () => {
      setIsPlayerFullscreen(false);
      wasAppFullscreenRef.current = false;
    };

    onEvent("fullscreen_changed", onChanged);
    onEvent("fullscreen_failed", onFailed);

    return () => {
      offEvent("fullscreen_changed", onChanged);
      offEvent("fullscreen_failed", onFailed);
    };
  }, [hasTelegramFallback, isPlayerFullscreen]);

  // Регистрируем состояние для перехвата кнопки "Назад" Telegram (Escape)
  useEffect(() => {
    if (!hasTelegramFallback) return;

    setPlayerFullscreenState(isPlayerFullscreen, handleExitFullscreen);

    return () => setPlayerFullscreenState(false, null);
  }, [hasTelegramFallback, isPlayerFullscreen, handleExitFullscreen]);

  // Escape для выхода из fullscreen
  useEffect(() => {
    if (!isDesktopPlatform || !isPlayerFullscreen) return;

    const onKeyDown = (event) => {
      if (event.key !== "Escape") return;

      const el = document.activeElement;
      if (
        el?.tagName === "INPUT" ||
        el?.tagName === "TEXTAREA" ||
        el?.tagName === "SELECT" ||
        el?.isContentEditable
      ) {
        return;
      }

      handleExitFullscreen();
      event.preventDefault();
      event.stopPropagation();
    };

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isDesktopPlatform, isPlayerFullscreen, handleExitFullscreen]);

  return {
    isPlayerFullscreen,
    togglePlayerFullscreen,
    hasTelegramFallback,
  };
}
